// Libs
import { Box, Tab, Tabs } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import classNames from 'classnames/bind';
// Components, Layouts, Pages
// Others
import { ITabItem } from '~/utils/interface/common';
import { QUERY_KEY_TAB } from '~/utils/constants/common';
// Styles, images, icons
import styles from './BaseTab.module.scss';

type Props = {
  items: ITabItem[];
  bordered?: boolean;
  enableUrlSync?: boolean;
  childrenAction?: React.ReactNode;
  onChange?: (tab: string) => void;
};

const cx = classNames.bind(styles);

const BaseTab = (props: Props) => {
  //#region Destructuring Props
  const { items, bordered = true, enableUrlSync = true, childrenAction, onChange } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['common/base']);
  const [searchParams, setSearchParams] = useSearchParams();

  const defaultTabParam = useMemo(() => items[0]?.tabParam, [items]);
  const tabParamFromURL =
    enableUrlSync && searchParams.get(QUERY_KEY_TAB) ? searchParams.get(QUERY_KEY_TAB) : defaultTabParam;
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [currentTab, setCurrentTab] = useState<string | null>(tabParamFromURL);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    if (!enableUrlSync) {
      setCurrentTab(defaultTabParam);
      return;
    }

    const newTabParam = searchParams.get(QUERY_KEY_TAB) || defaultTabParam;
    setCurrentTab(newTabParam);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, defaultTabParam]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleChange = (event: React.SyntheticEvent, newValue: string) => {
    !enableUrlSync && setCurrentTab(newValue);
    enableUrlSync && handleSetParams(newValue);
    onChange && onChange(newValue);
  };

  const handleSetParams = (tabParam: string, replace: boolean = false) => {
    if (!tabParam) return;

    const currentParams = new URLSearchParams();
    currentParams.set(QUERY_KEY_TAB, tabParam);

    if (replace) {
      setSearchParams(currentParams, { replace: true, preventScrollReset: true });
      return;
    }

    setSearchParams(currentParams, { preventScrollReset: true });
  };
  //#endregion Handle Function

  return (
    <Box
      component={'div'}
      id='baseTabComponent'
      className={cx('container', { bordered: bordered })}
      sx={{
        height: '100%',
      }}
    >
      <Tabs value={currentTab} onChange={handleChange} variant='scrollable' scrollButtons='auto'>
        {items.map((tabItem, index) => (
          <Tab key={index} value={tabItem.tabParam} label={t(tabItem.tabName)} disabled={tabItem.disable} />
        ))}
      </Tabs>

      <Box>{childrenAction}</Box>
    </Box>
  );
};

export default BaseTab;
